/**
 * Timeline Component
 *
 * This is the main component for the Timeline feature, which allows users to:
 * - Create and manage plot lines (horizontal rows)
 * - Create and manage chapters (vertical columns)
 * - Create, edit, and organize plot cards within the timeline grid
 * - Apply story templates to timelines
 * - Perform bulk operations on cards, chapters, and timelines
 *
 * The component uses React Query for data fetching and state management,
 * with optimized caching and mutation strategies.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useLocation, useParams } from "react-router-dom";

// Lucide icons
import {
    ArrowUpDown,
    BookAIcon,
    HelpCircle,
    Plus,
    RefreshCw,
    Rows,
    ZoomIn,
    ZoomOut
} from "lucide-react";

// UI components
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from '@/components/ui/use-toast';
import { cn } from "@/lib/utils";

// Timeline service and queries
import {
    secureTimelineKeys,
    useCreatePlotCard,
    useDeletePlotCard,
    useDeletePlotLine,
    useMoveCardsToChapter,
    useRefreshTimeline,
    useReorderChapters,
    useReorderTimelines,
    useSecureTimeline,
    useUpdatePlotCard,
    useUpdatePlotLine
} from "@/services/query/secure-timeline-queries";

// Hooks
import { useAuth } from "@/features/auth";
import { useProjectMeta } from '@/hooks/meta/useProjectMeta';
import { useCreateChapter, useDeleteChapter, useDeleteChapters, useUpdateChapter } from '@/hooks/useChapters';
import { useDialog } from "@/hooks/useDialog";
import { useLocalStorage } from '@/hooks/useLocalStorage';

// Types
import type { CardType, Chapter, Entity, PlotCard, PlotLine, Tag } from "./types";

// Timeline components
import { UnifiedSearch } from "@/components/shared/UnifiedSearch";
import { useQueryClient } from "@tanstack/react-query";
import { BulkActionsBar } from './components/BulkActionsBar';
import { BulkDeleteDialog } from './components/BulkDeleteDialog';
import { CreateTimelineDialog } from './components/CreateTimelineDialog';
import { CreateTimelineWithChaptersDialog } from './components/CreateTimelineWithChaptersDialog';
import { HelpDialog } from './components/HelpDialog';
import { PlotCardDetailsModal } from './components/PlotCardDetailsModal';
import { AddPlotCardDialogContent } from "./components/PlotCardDialog";
import { ReorderChaptersDialog } from './components/ReorderChaptersDialog';
import { ReorderTimelinesDialog } from './components/ReorderTimelinesDialog';
import { SelectionPanel } from './components/SelectionPanel';
import { TemplateDialog } from './components/TemplateDialog';
import { TimelineFilter } from './components/TimelineFilter';
import { TimelineGrid } from './components/TimelineGrid';
import { TimelineGuidedSetup } from './components/TimelineGuidedSetup';

// Services
import { supabase } from '@/services/supabase/supabase';
import { templateService } from '@/services/template';






/**
 * Main Timeline component
 * Manages the timeline interface and all related operations
 */
export const Timeline = () => {
    const { projectId } = useParams();
    const { session, isLoading: isSessionLoading } = useAuth();

    const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
    const [showGuidedSetup, setShowGuidedSetup] = useState(true);
    const isInitialized = true; // With the new auth system, we're always initialized
    const { openDialog, registerDialog } = useDialog();
    const { toast } = useToast();
    const location = useLocation();
    const prevLocationRef = useRef(location.pathname);
    const { value: compactMode, setValue: setCompactMode } = useLocalStorage('timeline_compact_mode', false);
    const queryClient = useQueryClient();
    // const { hasAIFeatures } = useLicense(); // Removed unused hook

    // Get current story ID from project metadata
    const { currentStoryId } = useProjectMeta(projectId || null);
    const deleteChaptersMutation = useDeleteChapters();

    // Fetch timeline data using the secure timeline query hook
    const {
        data: timelineState,
        isLoading,
        error: timelineError,
        refetch: refetchTimeline
    } = useSecureTimeline(projectId || '');

    // State for managing the current story ID
    const [storyId, setStoryId] = useState<string>('');

    // Update storyId from timelineState when it changes
    useEffect(() => {
        if (timelineState?.storyId && timelineState.storyId !== storyId) {
            console.log("Setting storyId from timelineState:", timelineState.storyId);
            setStoryId(timelineState.storyId);
        }
    }, [timelineState?.storyId, storyId]);

    // Log the current state for debugging
    useEffect(() => {
        console.log("Timeline IDs:", {
            projectId,
            storyId,
            currentStoryId,
            timelineStateId: timelineState?.storyId
        });
    }, [projectId, storyId, currentStoryId, timelineState?.storyId]);


    // Error handling


    // Search and filtering
    const [searchFilter, setSearchFilter] = useState("");
    const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

    // UI state
    const [isSaving, setIsSaving] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(100);
    const [viewMode] = useState<"grid" | "gantt">("grid");

    // Selection state
    const [selectedItems, setSelectedItems] = useState<PlotCard[]>([]);
    const [selectedTimelines, setSelectedTimelines] = useState<PlotLine[]>([]);
    const [selectedChapters, setSelectedChapters] = useState<Chapter[]>([]);

    // AI improvement dialog state - Removed

    // Dialog state
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState<{
        type: 'timelines' | 'chapters' | 'cards';
        open: boolean
    }>({ type: 'cards', open: false });
    const [isSelectionPanelOpen, setIsSelectionPanelOpen] = useState(false);
    const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
    const [cardToViewDetails, setCardToViewDetails] = useState<PlotCard | null>(null);
    const [showHelp, setShowHelp] = useState(false);
    const [showReorderChaptersDialog, setShowReorderChaptersDialog] = useState(false);
    const [showReorderTimelinesDialog, setShowReorderTimelinesDialog] = useState(false);
    const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
    const [isCreateTimelineDialogOpen, setIsCreateTimelineDialogOpen] = useState(false);
    const [isCreateWithChaptersDialogOpen, setIsCreateWithChaptersDialogOpen] = useState(false);
    const [selectedTimelineId, setSelectedTimelineId] = useState<number | null>(null);

    // Refs for managing refresh state
    const refreshInProgressRef = useRef(false);
    const lastRefreshTimeRef = useRef(0);



    // Define filteredCards here with proper null checks
    const filteredCards = React.useMemo(() => {
        if (!timelineState?.plotCards) return [];

        // Debug: Log filtering state
        if (selectedEntities.length > 0 || selectedTags.length > 0) {
            console.log(`Filtering ${timelineState.plotCards.length} cards with ${selectedEntities.length} entities and ${selectedTags.length} tags`);
        }

        const filtered = timelineState.plotCards.filter(card => {
            // Text search filter
            const matchesSearch = searchFilter === "" ||
                card.title?.toLowerCase().includes(searchFilter.toLowerCase()) ||
                card.content?.toLowerCase().includes(searchFilter.toLowerCase());

            // Entity filter - card matches if it has ANY of the selected entities
            const matchesEntities = selectedEntities.length === 0 ||
                (card.entities && card.entities.length > 0 &&
                    selectedEntities.some(entity =>
                        card.entities?.some(cardEntity => cardEntity.id === entity.id)
                    ));

            // Tag filter - card matches if it has ANY of the selected tags
            const matchesTags = selectedTags.length === 0 ||
                (card.tags && card.tags.length > 0 &&
                    selectedTags.some(tag =>
                        card.tags?.some(cardTag => cardTag.id === tag.id)
                    ));

            // Debug: Log detailed filtering for specific cards if needed
            if ((selectedEntities.length > 0 || selectedTags.length > 0) &&
                card.id % 10 === 0) { // Only log every 10th card to avoid console spam
                console.log(`Card ${card.id} (${card.title}): matchesEntities=${matchesEntities}, matchesTags=${matchesTags}`);
                if (card.entities) {
                    console.log(`  Entities: ${card.entities.map(e => e.id).join(', ')}`);
                }
                if (card.tags) {
                    console.log(`  Tags: ${card.tags.map(t => t.id).join(', ')}`);
                }
            }

            return matchesSearch && matchesEntities && matchesTags;
        });

        // Debug: Log filtering results
        if (selectedEntities.length > 0 || selectedTags.length > 0) {
            console.log(`Filtered down to ${filtered.length} cards`);
        }

        return filtered;
    }, [timelineState?.plotCards, searchFilter, selectedEntities, selectedTags]);

    // Use secure timeline mutation hooks
    const updatePlotCardMutation = useUpdatePlotCard();
    const deletePlotCardMutation = useDeletePlotCard();
    const moveCardsToChapterMutation = useMoveCardsToChapter();
    const refreshTimelineMutation = useRefreshTimeline();

    const createPlotCardMutation = useCreatePlotCard();
    const reorderTimelinesMutation = useReorderTimelines();
    const reorderChaptersMutation = useReorderChapters();

    // Import queries/mutations for plot lines
    const updatePlotLineMutation = useUpdatePlotLine();
    const deletePlotLineMutation = useDeletePlotLine();

    const handleRefreshTimeline = useCallback(() => {
        if (projectId) {
            refreshTimelineMutation.mutate({
                projectId: projectId
            }, {
                onSuccess: () => {
                    refetchTimeline();
                }
            });
        }
    }, [projectId, refreshTimelineMutation, refetchTimeline]);

    // Handler for opening AI Timeline Dialog using the centralized dialog system
    /*
    const handleOpenAITimelineDialog = useCallback(() => {
        if (!projectId) return;
    
        const timelines = timelineState?.plotLines?.map(t => ({
            id: t.id.toString(),
            title: t.name,
            description: t.description
        })) || [];
    
        openDialog('ai-timeline', {
            projectId,
            availableTimelines: timelines,
            onRefreshTimeline: handleRefreshTimeline
        }, (data: { timelineId: string }) => {
            console.log('AI Timeline created:', data.timelineId);
            // Refresh timeline data after creation
            handleRefreshTimeline();
            toast({
                title: "Success",
                description: "AI timeline created successfully",
            });
        });
    }, [projectId, timelineState?.plotLines, openDialog, handleRefreshTimeline, toast]);
    */

    // Chapter mutations
    const createChapterMutation = useCreateChapter();
    const updateChapterMutation = useUpdateChapter();
    const deleteChapterMutation = useDeleteChapter();

    const timelineRef = useRef<HTMLDivElement>(null);

    // Register dialogs
    useEffect(() => {
        if (registerDialog) {
            console.log('Registering dialogs in Timeline component');
            registerDialog('plotcard', AddPlotCardDialogContent);

            // Create AI Timeline DialogContent component (content only, no Dialog wrapper)
            /*
            const AITimelineDialogContent = ({ onClose, data, onSave }: any) => {
                const [prompt, setPrompt] = useState('');
                const [isLoading, setIsLoading] = useState(false);
                const [isCreated, setIsCreated] = useState(false);
                const [error, setError] = useState<string | null>(null);
                const [streamingContent, setStreamingContent] = useState('');
                const [currentStep, setCurrentStep] = useState('');
                const [progress, setProgress] = useState(0);
    
                // Get current story ID and tenant ID
                const { data: currentStory } = useCurrentStory(data.projectId, { includeFull: false });
                const { data: tenantId } = useCurrentTenantId();
    
    
                const handleSubmit = async (e: React.FormEvent) => {
                    e.preventDefault();
                    if (!prompt.trim() || isLoading) return;
    
                    // Check if we have a current story
                    if (!currentStory?.id) {
                        setError('No story selected. Please select a story first.');
                        return;
                    }
    
                    setIsLoading(true);
                    setError(null);
                    setStreamingContent('');
                    setCurrentStep('Initializing...');
                    setProgress(0);
    
    
                    try {
                        // Import the AI service
                        const { aiTimelineService } = await import('@/services/ai-service');
    
                        console.log(`🤖 Sending AI timeline creation request:`, {
                            projectId: data.projectId,
                            storyId: currentStory.id,
                            promptLength: prompt.length
                        });
    
                        // Use the dedicated timeline streaming API with new format
                        const response = await aiTimelineService.streamCreateTimeline(
                            prompt.trim(),
                            currentStory.id.toString(),
                            data.projectId,
                            tenantId || undefined
                        );
    
                        // Handle streaming response for timeline creation
                        const reader = response.body?.getReader();
                        if (!reader) {
                            throw new Error('No response body');
                        }
    
                        let hasReceivedContent = false;
    
    
                        while (true) {
                            const { done, value } = await reader.read();
                            if (done) break;
    
                            const chunk = new TextDecoder().decode(value);
                            const lines = chunk.split('\n').filter(line => line.trim() !== '');
    
                            for (const line of lines) {
                                if (line.startsWith('data: ')) {
                                    try {
                                        const data = JSON.parse(line.slice(6));
    
                                        if (data.type === 'status') {
                                            const status = data.data?.status;
                                            const message = data.data?.message || 'Processing...';
    
                                            setCurrentStep(message);
    
                                            // Update progress based on status
                                            switch (status) {
                                                case 'start':
                                                    setProgress(10);
                                                    setStreamingContent('🚀 Timeline generation started...');
                                                    break;
                                                case 'ai_working':
                                                    setProgress(30);
                                                    setStreamingContent('🤔 AI is analyzing your request...');
                                                    break;
                                                case 'summary_generated':
                                                case 'summary':
                                                    // const summary = data.data?.summary || '';
                                                    // setTimelineSummary(summary);
                                                    setStreamingContent('📝 Timeline summary generated');
                                                    hasReceivedContent = true;
                                                    break;
                                                case 'applying_updates':
                                                    setProgress(80);
                                                    setStreamingContent('💾 Applying timeline updates to database...');
                                                    break;
                                                case 'stop':
                                                    setProgress(100);
                                                    setCurrentStep('Timeline creation complete');
                                                    setStreamingContent('✅ Timeline created successfully!');
                                                    hasReceivedContent = true;
    
                                                    // Set success state
                                                    setIsCreated(true);
                                                    setIsLoading(false);
    
                                                    console.log('✅ AI timeline creation completed successfully');
                                                    // Dialog stays open - user must manually close
                                                    return;
                                                default:
                                                    setStreamingContent(message);
                                            }
                                        } else if (data.type === 'complete') {
                                            setProgress(100);
                                            setCurrentStep(data.data?.message || 'Timeline creation complete');
    
                                            // Extract timeline information from the response
                                            const toolResults = data.data?.toolResults || [];
                                            const timelineResult = toolResults.find((result: any) =>
                                                result.toolName === 'createTimelineWithPlotcards'
                                            );
    
                                            if (timelineResult?.success && timelineResult?.result?.timeline?.id) {
                                                const timeline = timelineResult.result.timeline;
                                                const resultTimelineId = timeline.id;
                                                console.log(`✅ AI timeline created successfully:`, {
                                                    timelineId: resultTimelineId,
                                                    title: timeline.title,
                                                    plotcardsCount: timelineResult.result.plotcards?.length || 0
                                                });
    
                                                // Set success state
                                                setIsCreated(true);
                                                const successMessage = data.data?.response ||
                                                    `✅ Timeline "${timeline.title || 'New Timeline'}" created successfully!`;
                                                setStreamingContent(successMessage);
                                                setIsLoading(false);
    
                                                // Dialog stays open - user must manually close
                                                return;
                                            } else {
                                                // Check if we have any successful tool results
                                                const hasSuccess = toolResults.some((result: any) => result.success);
    
                                                if (hasSuccess) {
                                                    // Timeline was processed but we don't have the ID
                                                    console.log(`✅ AI timeline created (no ID returned)`);
                                                    setIsCreated(true);
                                                    const successMessage = data.data?.response ||
                                                        '✅ Timeline created successfully! Your new timeline is ready.';
                                                    setStreamingContent(successMessage);
                                                    setIsLoading(false);
                                                    // Dialog stays open - user must manually close
                                                    return;
                                                } else {
                                                    throw new Error('AI agent did not create a timeline. Please try rephrasing your request.');
                                                }
                                            }
                                        } else if (data.type === 'error') {
                                            throw new Error(data.data?.error || 'Unknown error occurred');
                                        }
                                    } catch (parseError) {
                                        console.error('Error parsing SSE data:', parseError);
                                    }
                                }
                            }
                        }
    
                        // If we get here without a timeline being processed, treat as error
                        if (!hasReceivedContent) {
                            throw new Error('AI timeline creation completed but no content was received');
                        }
    
                    } catch (error) {
                        console.error('Error creating AI timeline:', error);
                        const errorMessage = error instanceof Error ? error.message : 'Failed to create timeline';
                        setError(errorMessage);
                        setCurrentStep('Error occurred');
                        setProgress(0);
                    } finally {
                        setIsLoading(false);
                    }
                };
    
                return (
                    <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-popover border-border text-foreground shadow-2xl">
                        <DialogHeader>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 shadow-inner">
                                    <AnimatePresence mode="wait">
                                        {isLoading ? (
                                            <motion.div
                                                key="loading"
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.8 }}
                                            >
                                                <Brain className="h-5 w-5 text-primary" />
                                            </motion.div>
                                        ) : isCreated ? (
                                            <motion.div
                                                key="success"
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.8 }}
                                            >
                                                <CheckCircle className="h-5 w-5 text-emerald-500" />
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                key="idle"
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.8 }}
                                            >
                                                <Wand2 className="h-5 w-5 text-primary" />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                                <div className="flex-1 space-y-1">
                                    <DialogTitle className="flex items-center gap-2 text-xl font-semibold tracking-tight">
                                        AI Timeline Creation
                                        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 px-2 py-0.5 text-[10px] tracking-wider font-medium uppercase">
                                            Pro
                                        </Badge>
                                    </DialogTitle>
                                    <DialogDescription className="text-muted-foreground/80">
                                        {isLoading ? (
                                            <span className="text-primary font-medium animate-pulse">
                                                AI is crafting your timeline...
                                            </span>
                                        ) : isCreated ? (
                                            <span className="text-emerald-500 font-medium">
                                                Your timeline has been created successfully!
                                            </span>
                                        ) : (
                                            "Describe your story vision and let AI bring it to life"
                                        )}
                                    </DialogDescription>
                                </div>
                            </div>
    
                            // Progress Section
                            <AnimatePresence>
                                {isLoading && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="p-4 bg-muted/50 border border-border rounded-xl mb-4"
                                    >
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <motion.div
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                                    >
                                                        <Loader2 className="h-4 w-4 text-primary" />
                                                    </motion.div>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {currentStep || 'Initializing AI...'}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-muted-foreground font-mono">
                                                    {progress}%
                                                </span>
                                            </div>
    
                                            <Progress value={progress} className="h-1.5 bg-white/5" />
    
                                            {streamingContent && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="text-sm text-muted-foreground leading-relaxed pl-6 border-l-2 border-primary/20"
                                                >
                                                    {streamingContent}
                                                </motion.div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </DialogHeader>
    
                        // Form Content
                        <div className="space-y-4 py-2">
                            <AnimatePresence mode="wait">
                                {!isLoading && !isCreated ? (
                                    <motion.form
                                        key="form"
                                        id="timeline-form"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        onSubmit={handleSubmit}
                                        className="space-y-4"
                                    >
                                        <div className="space-y-3">
                                            <Label htmlFor="prompt" className="text-sm font-medium text-foreground/90">
                                                Describe your timeline vision
                                            </Label>
                                            <Textarea
                                                id="prompt"
                                                value={prompt}
                                                onChange={(e) => setPrompt(e.target.value)}
                                                placeholder="Tell me about your story... What genre? What's the main conflict? Who are the key characters? The more details you provide, the better your timeline will be!"
                                                className="min-h-[140px] resize-none bg-background border-border focus:border-primary/50 focus:ring-0 placeholder:text-muted-foreground/40 text-sm leading-relaxed rounded-xl shadow-inner"
                                                disabled={isLoading}
                                                required
                                            />
                                            <div className="flex items-start gap-3 p-3 bg-muted/5 border border-border rounded-xl">
                                                <Lightbulb className="h-4 w-4 text-amber-500/80 mt-0.5 flex-shrink-0" />
                                                <div className="text-xs text-muted-foreground leading-normal">
                                                    <strong className="text-foreground/80">Pro tip:</strong> Include genre, main characters, setting, and key plot points for the best results.
                                                </div>
                                            </div>
                                        </div>
                                    </motion.form>
                                ) : null}
                            </AnimatePresence>
                        </div>
    
                        // Error Display
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 rounded-xl bg-destructive/10 border border-destructive/20"
                            >
                                <div className="flex items-start gap-2">
                                    <X w-4 className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-destructive font-medium">{error}</p>
                                </div>
                            </motion.div>
                        )}
    
                        // Success Message
                        {isCreated && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/20">
                                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-emerald-500">Timeline Created Successfully</p>
                                        <p className="text-sm text-muted-foreground">Your new timeline is ready to explore.</p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
    
                        <DialogFooter className="mt-4">
                            <AnimatePresence mode="wait">
                                {isCreated ? (
                                    <Button
                                        type="button"
                                        onClick={onClose}
                                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-lg shadow-lg shadow-primary/20"
                                    >
                                        <CheckCircle className="mr-2 h-4 w-4" />
                                        View Timeline
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        form="timeline-form"
                                        disabled={!prompt.trim() || isLoading}
                                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-lg shadow-lg shadow-primary/20"
                                        onClick={handleSubmit}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Creating Timeline...
                                            </>
                                        ) : (
                                            <>
                                                <Wand2 className="mr-2 h-4 w-4" />
                                                Create AI Timeline
                                            </>
                                        )}
                                    </Button>
                                )}
                            </AnimatePresence>
                        </DialogFooter>
                    </DialogContent>
                );
            };
    
            registerDialog('ai-timeline', AITimelineDialogContent);
            */

            // Create a simple inline MoveCardsDialog component
            interface MoveCardsDialogData {
                title?: string;
                description?: string;
                chapters: Chapter[];
            }

            const MoveCardsDialogContent = ({ onClose, data, onSave }: { onClose: () => void; data: MoveCardsDialogData; onSave: (data: { chapterId: number }) => void }) => {
                const [selectedChapterId, setSelectedChapterId] = useState<number | null>(null);
                const { title, description, chapters } = data;

                console.log('MoveCardsDialog: Received chapters data:', chapters);

                const handleSubmit = (e: React.FormEvent) => {
                    e.preventDefault();
                    if (selectedChapterId !== null) {
                        onSave({ chapterId: selectedChapterId });
                        onClose();
                    }
                };

                return (
                    <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                            <DialogTitle>{title || 'Move Cards'}</DialogTitle>
                        </DialogHeader>

                        <form onSubmit={handleSubmit}>
                            <div className="grid gap-4 py-4">
                                <p className="text-sm text-muted-foreground mb-4">
                                    {description || 'Select a chapter to move the selected cards to:'}
                                </p>

                                <div className="grid grid-cols-4 items-center gap-4">
                                    <Label htmlFor="chapter" className="text-right">
                                        Chapter
                                    </Label>
                                    <Select
                                        onValueChange={(value) => setSelectedChapterId(parseInt(value))}
                                        required
                                    >
                                        <SelectTrigger className="col-span-3">
                                            <SelectValue placeholder="Select a chapter" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {chapters && chapters.map((chapter: Chapter) => {
                                                // Skip rendering SelectItem if id is undefined or empty
                                                if (!chapter.id) return null;
                                                return (
                                                    <SelectItem key={chapter.id} value={chapter.id.toString()}>
                                                        {chapter.name || chapter.title}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={onClose}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={selectedChapterId === null}>
                                    Move Cards
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                );
            };

            registerDialog('move-cards', MoveCardsDialogContent);
        }
    }, [registerDialog]);

    // Initialize UI state from timeline settings when it changes
    useEffect(() => {
        if (timelineState?.settings) {
            console.log('Initializing UI state from timeline settings:', timelineState.settings);

            // Initialize zoom level (convert from decimal to percentage)
            if (timelineState.settings.zoom_level) {
                setZoomLevel(Math.round(timelineState.settings.zoom_level * 100));
            }
        }
    }, [timelineState?.settings]);

    // First load effect
    useEffect(() => {
        // This ensures initial load and proper data fetch
        if (isInitialized && !isSessionLoading && session && projectId) {
            console.log("Timeline component mounted with session and projectId");

            const initializeTimeline = async () => {
                // Skip if a refresh is already in progress or if we refreshed very recently
                if (refreshInProgressRef.current) {
                    console.log("Skipping refresh because one is already in progress");
                    return;
                }

                const now = Date.now();
                // Prevent refreshes from happening more than once every 2 seconds
                if (now - lastRefreshTimeRef.current < 2000) {
                    console.log("Skipping refresh because one happened recently");
                    return;
                }

                // Most importantly, skip the refetch if we already have data
                // This prevents unnecessary API calls when revisiting the page
                if (timelineState) {
                    console.log("Skipping refresh because we already have timeline data");
                    return;
                }

                try {
                    refreshInProgressRef.current = true;
                    lastRefreshTimeRef.current = now;

                    // Only perform the refetch if we don't have data
                    await refetchTimeline();
                } catch (err) {
                    console.error("Error initializing timeline data:", err);
                    // Error will be handled by the main useTimeline hook
                } finally {
                    refreshInProgressRef.current = false;
                }
            };

            initializeTimeline();
        }
    }, [isInitialized, isSessionLoading, session, projectId, storyId, currentStoryId, refetchTimeline, timelineState]);

    // Effect to detect navigation back to the timeline
    useEffect(() => {
        if (location.pathname !== prevLocationRef.current) {
            // We detect that user navigated to this page from somewhere else
            if (prevLocationRef.current && location.pathname.includes('/timeline/')) {
                console.log("Navigated back to timeline, checking if data needs refresh");

                // Optionally refresh data when navigating back to timeline
                // You can add conditions here for when to refresh vs when to use cache
            }

            prevLocationRef.current = location.pathname;
        }
    }, [location.pathname, projectId]);

    // Log the secure timeline data for comparison
    useEffect(() => {
        if (timelineState) {
            console.log('Regular Timeline State:', timelineState);

            // Debug logging for entities and tags in plotcards
            if (timelineState.plotCards && timelineState.plotCards.length > 0) {
                const cardsWithEntities = timelineState.plotCards.filter(card => card.entities && card.entities.length > 0);
                const cardsWithTags = timelineState.plotCards.filter(card => card.tags && card.tags.length > 0);

                console.log(`Found ${cardsWithEntities.length} cards with entities out of ${timelineState.plotCards.length} total cards`);
                console.log(`Found ${cardsWithTags.length} cards with tags out of ${timelineState.plotCards.length} total cards`);

                if (cardsWithEntities.length > 0) {
                    console.log('Sample card with entities:', cardsWithEntities[0]);
                }

                if (cardsWithTags.length > 0) {
                    console.log('Sample card with tags:', cardsWithTags[0]);
                }
            }
        }
    }, [timelineState]);



    // Entity and tag filter handlers
    const handleEntitySelect = useCallback((entity: Entity) => {
        setSelectedEntities(prev => [...prev, entity]);

        // Analytics
        console.log(`Added entity filter: ${entity.name} (${entity.id})`);

        // Debug: Check if any cards have this entity
        if (timelineState?.plotCards) {
            const matchingCards = timelineState.plotCards.filter(card =>
                card.entities && card.entities.some(e => e.id === entity.id)
            );
            console.log(`Found ${matchingCards.length} cards with entity ${entity.name} (${entity.id})`);
            if (matchingCards.length > 0) {
                console.log('Sample matching card:', matchingCards[0]);
            }
        }
    }, [timelineState?.plotCards]);

    const handleEntityRemove = useCallback((entityId: number) => {
        setSelectedEntities(prev => {
            const entityToRemove = prev.find(e => e.id === entityId);
            if (entityToRemove) {
                console.log(`Removed entity filter: ${entityToRemove.name} (${entityToRemove.id})`);
            }
            return prev.filter(entity => entity.id !== entityId);
        });
    }, []);

    const handleTagSelect = useCallback((tag: Tag) => {
        setSelectedTags(prev => [...prev, tag]);

        // Analytics
        console.log(`Added tag filter: ${tag.name} (${tag.id})`);

        // Debug: Check if any cards have this tag
        if (timelineState?.plotCards) {
            const matchingCards = timelineState.plotCards.filter(card =>
                card.tags && card.tags.some(t => t.id === tag.id)
            );
            console.log(`Found ${matchingCards.length} cards with tag ${tag.name} (${tag.id})`);
            if (matchingCards.length > 0) {
                console.log('Sample matching card:', matchingCards[0]);
            }
        }
    }, [timelineState?.plotCards]);

    const handleTagRemove = useCallback((tagId: number) => {
        setSelectedTags(prev => {
            const tagToRemove = prev.find(t => t.id === tagId);
            if (tagToRemove) {
                console.log(`Removed tag filter: ${tagToRemove.name} (${tagToRemove.id})`);
            }
            return prev.filter(tag => tag.id !== tagId);
        });
    }, []);

    const handleClearFilters = useCallback(() => {
        if (selectedEntities.length > 0 || selectedTags.length > 0) {
            console.log(`Cleared all filters: ${selectedEntities.length} entities, ${selectedTags.length} tags`);
            setSelectedEntities([]);
            setSelectedTags([]);
        }
    }, [selectedEntities, selectedTags]);



    const handleRefreshData = useCallback(async () => {
        if (!projectId) return;
        const storyId = timelineState?.storyId;

        console.log(`Refreshing data for projectId: ${projectId}, storyId: ${storyId}`);
        // Skip if a refresh is already in progress
        if (refreshInProgressRef.current) {
            console.log("Skipping manual refresh because one is already in progress");
            return;
        }

        const now = Date.now();
        // Prevent manual refreshes from happening more than once every 500ms
        if (now - lastRefreshTimeRef.current < 500) {
            console.log("Skipping manual refresh because one happened recently");
            return;
        }

        setIsSaving(true);
        refreshInProgressRef.current = true;
        lastRefreshTimeRef.current = now;

        try {
            // Call to refreshStoryContext removed as it's deprecated


            // Explicitly refetch the timeline data to update the UI
            refreshTimelineMutation.mutate({
                projectId: projectId || ''
            }, {
                onSuccess: () => {
                    setIsSaving(false);
                    toast({
                        title: "Timeline Refreshed",
                        description: "Story context has been synced and timeline updated.",
                        duration: 3000
                    });
                },
                onError: (error) => {
                    console.error('Error refreshing timeline data:', error);
                    setIsSaving(false);
                    toast({
                        title: "Refresh failed",
                        description: error instanceof Error ? error.message : "An unknown error occurred",
                        variant: "destructive",
                        duration: 3000
                    });
                },
                onSettled: () => {
                    refreshInProgressRef.current = false;
                }
            });
        } catch (error) {
            console.error('Error refreshing timeline:', error);
            setIsSaving(false);
            refreshInProgressRef.current = false;
            toast({
                title: "Refresh failed",
                description: error instanceof Error ? error.message : "An unknown error occurred",
                variant: "destructive",
            });
        }
    }, [projectId, refreshTimelineMutation, toast]); // Removed session from deps as it is not used anymore


    const handleAddChapter = useCallback(() => {
        if (!projectId) return;
        console.log('Opening chapter dialog for chapter');

        openDialog('form',
            {
                title: 'Create New Chapter',
                description: 'Create a new chapter with description and outline',
                submitText: 'Create Chapter',
                fields: [
                    { name: 'name', label: 'Chapter Name', type: 'text', required: true, placeholder: 'Chapter 1: The Beginning' },
                    { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe what happens in this chapter...' },
                    { name: 'outline', label: 'Chapter Outline', type: 'textarea', placeholder: 'Write your detailed chapter outline here...' }
                ]
            },
            async (data: { name: string; description: string; outline?: string }) => {
                try {
                    console.log('Saving chapter:', data);
                    setIsSaving(true);

                    // Get the storyId from the timelineState
                    const { storyId } = timelineState || {};

                    if (!storyId) {
                        console.error('No story ID available');
                        setIsSaving(false);
                        return;
                    }

                    // Use the createChapter mutation with proper cache invalidation
                    const newChapter = await createChapterMutation.mutateAsync({
                        storyId: parseInt(storyId),
                        title: data.name,
                        description: data.description,
                        projectId
                    });

                    // If outline is provided, create the chapter outline
                    if (data.outline && data.outline.trim()) {
                        try {
                            const { createChapterOutline } = await import('@/services/supabase/chapter-outlines');
                            await createChapterOutline(newChapter.id, data.outline);
                            console.log('Chapter outline created successfully');
                        } catch (outlineError) {
                            console.error('Error creating chapter outline:', outlineError);
                            // Don't fail the entire operation if outline creation fails
                        }
                    }

                    console.log('Chapter created successfully');
                    setIsSaving(false);
                    toast({
                        title: "Chapter created",
                        description: `Chapter "${data.name}" has been created.`,
                        duration: 3000
                    });
                } catch (error) {
                    console.error('Error creating chapter:', error);
                    setIsSaving(false);
                    toast({
                        title: "Error creating chapter",
                        description: "There was an error creating the chapter. Please try again.",
                        variant: "destructive",
                        duration: 3000
                    });
                }
            }
        );
    }, [projectId, openDialog, timelineState, createChapterMutation, toast]);

    // Method to move cards to chapter using React Query with optimistic updates
    const moveCardsToChapter = useCallback((cardIds: number[], targetChapterId: number) => {
        if (!timelineState?.storyId) return;

        // Find the cards in the current state
        const cardsToMove = timelineState.plotCards.filter(card => cardIds.includes(card.id));
        if (cardsToMove.length === 0) {
            console.error(`Cards with IDs ${cardIds.join(', ')} not found in the current state`);
            return;
        }

        // Save the original cards state for potential rollback
        const originalCards = [...cardsToMove];

        // 1. Optimistically update the local state immediately
        const updatedCards = timelineState.plotCards.map(card => {
            if (cardIds.includes(card.id)) {
                // Make sure to preserve all card properties, especially the type
                return {
                    ...card,
                    chapter_id: targetChapterId,
                    // Explicitly preserve the type
                    type: card.type
                };
            }
            return card;
        });

        // Update the timelineState with the new cards array
        const updatedTimelineState = {
            ...timelineState,
            plotCards: updatedCards
        };

        // Use the queryClient to update the cache directly
        queryClient.setQueryData(
            secureTimelineKeys.timeline(projectId || ''),
            updatedTimelineState
        );

        // Clear selection after moving (optimistically)
        setSelectedItems([]);

        // 2. Make the API call in the background
        moveCardsToChapterMutation.mutate({
            cardIds,
            chapterId: targetChapterId,
            timelineId: timelineState.storyId,
            projectId: projectId || ''
        }, {
            // 3. Handle success - no need to update the UI again
            onSuccess: () => {
                console.log('Cards moved successfully');
                // Show a toast for bulk operations (unlike drag and drop)
                toast({
                    title: "Cards moved",
                    description: `${cardIds.length} cards have been moved to the selected chapter.`,
                    duration: 3000
                });
            },
            // 4. Handle error - revert the local state change
            onError: (err) => {
                console.error('Error moving cards to chapter:', err);

                // Revert the optimistic update by restoring the original cards
                const revertedCards = timelineState.plotCards.map(card => {
                    const originalCard = originalCards.find(oc => oc.id === card.id);
                    return originalCard || card;
                });

                // Update the timelineState with the reverted cards array
                const revertedTimelineState = {
                    ...timelineState,
                    plotCards: revertedCards
                };

                // Use the queryClient to update the cache with the reverted state
                queryClient.setQueryData(
                    secureTimelineKeys.timeline(projectId || ''),
                    revertedTimelineState
                );

                // Show an error toast
                toast({
                    title: "Error moving cards",
                    description: "There was an error moving the cards. The changes have been reverted.",
                    variant: "destructive",
                    duration: 3000
                });
            }
        });
    }, [timelineState, queryClient, moveCardsToChapterMutation, setSelectedItems, projectId, toast]);

    // Example: Updated method to handle settings update using React Query

    // --------- Other handlers to update using React Query ---------

    // Handler for handling selected cards
    const handleMoveSelectedCards = useCallback(() => {
        if (selectedItems.length === 0) return;

        // Get chapters from timelineState
        if (!timelineState?.chapters) return;

        openDialog(
            'move-cards',
            {
                title: 'Move Selected Cards',
                description: `Move ${selectedItems.length} selected card(s) to a different chapter`,
                chapters: timelineState.chapters
            },
            (data: { chapterId: number }) => {
                const cardIds = selectedItems.map(item => item.id);
                moveCardsToChapter(cardIds, data.chapterId);
            }
        );
    }, [selectedItems, timelineState?.chapters, openDialog, moveCardsToChapter]);

    // Handler for timeline selection - enables bulk selection mode
    const handleTimelineSelection = useCallback((plotLine: PlotLine) => {
        setSelectedTimelines(prev => {
            // Check if this timeline is already selected
            const isSelected = prev.some(t => t.id === plotLine.id);

            if (isSelected) {
                // If already selected, remove it (toggle off)
                return prev.filter(t => t.id !== plotLine.id);
            } else {
                // If not selected, add it to the selection
                return [...prev, plotLine];
            }
        });

        // Clear chapter selection when selecting timelines to avoid confusion
        if (selectedChapters.length > 0) {
            setSelectedChapters([]);
        }
    }, [selectedChapters]);

    // Handler for chapter selection - enables bulk selection mode
    const handleChapterSelection = useCallback((chapter: Chapter) => {
        setSelectedChapters(prev => {
            // Check if this chapter is already selected
            const isSelected = prev.some(c => c.id === chapter.id);

            if (isSelected) {
                // If already selected, remove it (toggle off)
                return prev.filter(c => c.id !== chapter.id);
            } else {
                // If not selected, add it to the selection
                return [...prev, chapter];
            }
        });

        // Clear timeline selection when selecting chapters to avoid confusion
        if (selectedTimelines.length > 0) {
            setSelectedTimelines([]);
        }
    }, [selectedTimelines]);

    // Handler for selecting all timelines
    const handleSelectAllTimelines = useCallback(() => {
        if (timelineState?.plotLines) {
            setSelectedTimelines(timelineState.plotLines);
        }
    }, [timelineState?.plotLines]);

    // Handler for selecting all chapters
    const handleSelectAllChapters = useCallback(() => {
        if (timelineState?.chapters) {
            setSelectedChapters(timelineState.chapters);
        }
    }, [timelineState?.chapters]);

    // Handler for bulk delete timelines
    const handleBulkDeleteTimelines = useCallback(() => {
        if (selectedTimelines.length === 0) return;

        setShowBulkDeleteDialog({ type: 'timelines', open: true });
    }, [selectedTimelines]);

    // Handler for bulk delete chapters
    const handleBulkDeleteChapters = useCallback(() => {
        if (selectedChapters.length === 0) return;

        setShowBulkDeleteDialog({ type: 'chapters', open: true });
    }, [selectedChapters]);

    // Define handleDeleteChapter before it's used in handleConfirmBulkDelete
    const handleDeleteChapter = useCallback(async (chapterId: number) => {
        if (!projectId || !timelineState?.storyId) return;

        try {
            console.log('Deleting chapter:', chapterId);
            setIsSaving(true);

            // Use the deleteChapter mutation with proper cache invalidation
            await deleteChapterMutation.mutateAsync({
                chapterId,
                storyId: timelineState.storyId,
                projectId
            });

            console.log('Chapter deleted successfully');
            setIsSaving(false);
            toast({
                title: "Chapter deleted",
                description: "The chapter has been deleted.",
                duration: 3000
            });
        } catch (error) {
            console.error('Error deleting chapter:', error);
            setIsSaving(false);
            toast({
                title: "Error deleting chapter",
                description: "There was an error deleting the chapter. Please try again.",
                variant: "destructive",
                duration: 3000
            });
        }
    }, [projectId, timelineState?.storyId, deleteChapterMutation, toast]);

    // Define handleDeletePlotLine before it's used in TimelineSidebar
    const handleDeletePlotLine = useCallback(async (plotLineId: number) => {
        if (!projectId || !timelineState?.storyId) return;

        try {
            console.log('Deleting plot line:', plotLineId);
            setIsSaving(true);

            // Use the deletePlotLineMutation that was defined at the top level of the component
            await deletePlotLineMutation.mutateAsync({
                lineId: plotLineId.toString(),
                projectId: projectId || ''
            });

            toast({
                title: "Plot line deleted",
                description: "The plot line has been deleted.",
                duration: 3000
            });
        } catch (error) {
            console.error('Error deleting plot line:', error);
            toast({
                title: "Error deleting plot line",
                description: "There was an error deleting the plot line. Please try again.",
                variant: "destructive",
                duration: 3000
            });
        } finally {
            setIsSaving(false);
        }
    }, [projectId, timelineState?.storyId, toast, deletePlotLineMutation]);

    // Handler for confirming bulk delete
    const handleConfirmBulkDelete = useCallback(async () => {
        if (!timelineState?.storyId) return;

        setIsSaving(true);

        try {
            if (showBulkDeleteDialog.type === 'timelines') {
                // Delete multiple timelines
                const timelineIds = selectedTimelines.map(t => t.id);

                // Execute deletions sequentially to avoid race conditions
                for (const id of timelineIds) {
                    await deletePlotLineMutation.mutateAsync({
                        lineId: id.toString(),
                        projectId: projectId || ''
                    });
                }

                // Clear selection after successful deletion
                setSelectedTimelines([]);

                toast({
                    title: "Timelines deleted",
                    description: `${timelineIds.length} timelines have been deleted.`,
                    duration: 3000
                });
            } else if (showBulkDeleteDialog.type === 'chapters') {
                // Delete multiple chapters
                const chapterIds = selectedChapters.map(c => c.id);

                // Use the new bulk delete mutation
                await deleteChaptersMutation.mutateAsync({
                    chapterIds,
                    storyId: timelineState.storyId,
                    projectId: projectId || ''
                });

                // Clear selection after successful deletion
                setSelectedChapters([]);

                toast({
                    title: "Chapters deleted",
                    description: `${chapterIds.length} chapters have been deleted.`,
                    duration: 3000
                });
            }
        } catch (error) {
            console.error('Error performing bulk delete:', error);
            toast({
                title: "Error",
                description: `There was an error deleting the selected ${showBulkDeleteDialog.type}.`,
                variant: "destructive",
                duration: 3000
            });
        } finally {
            setIsSaving(false);
            setShowBulkDeleteDialog({ ...showBulkDeleteDialog, open: false });
        }
    }, [showBulkDeleteDialog, selectedTimelines, selectedChapters, timelineState?.storyId, projectId, deletePlotLineMutation, deleteChaptersMutation, toast]);

    // Handler for deleting selected items
    const handleDeleteSelectedItems = useCallback(() => {
        if (selectedItems.length === 0 || !timelineState) return;

        // Store the number of items before clearing the selection
        const itemCount = selectedItems.length;

        // Clear the selection before deleting to avoid UI issues
        setSelectedItems([]);

        // Store storyId in a local const to ensure it's defined for all uses
        const storyId = timelineState.storyId;

        if (!storyId) {
            toast({
                title: "Error",
                description: "Cannot delete items: No story ID available",
                variant: "destructive"
            });
            return;
        }

        // Delete each item
        selectedItems.forEach(item => {
            deletePlotCardMutation.mutate({
                cardId: item.id.toString(),
                timelineId: storyId,
                projectId: projectId || ''
            });
        });

        toast({
            title: "Items deleted",
            description: `${itemCount} item(s) have been deleted.`,
            duration: 3000
        });
    }, [selectedItems, timelineState, deletePlotCardMutation, toast, projectId]);

    const handleAddPlotCard = useCallback((plotLineId: number, chapterId: number) => {
        if (!projectId || !timelineState) return;
        console.log('Opening plot card dialog', { plotLineId, chapterId });

        const chapterName = timelineState.chapters.find(c => c.id === chapterId)?.title || undefined;

        openDialog(
            'plotcard',
            {
                plotLineId,
                chapterId,
                defaultChapterName: chapterName,
                plotLines: timelineState.plotLines.map(pl => ({ id: pl.id, title: pl.name }))
            }, // Pass relevant data in the data object
            async (data: { title: string; content: string; type: CardType }) => {
                try {
                    console.log('Saving plot card:', { ...data, x: plotLineId, y: chapterId });
                    setIsSaving(true);

                    const storyId = timelineState.storyId;
                    if (!storyId) {
                        toast({
                            title: "Error",
                            description: "Cannot create card: No story ID available",
                            variant: "destructive"
                        });
                        setIsSaving(false);
                        return;
                    }

                    // Use mutation hook instead of direct service call
                    createPlotCardMutation.mutate({
                        timelineId: storyId,
                        plotLineId: plotLineId.toString(),
                        title: data.title,
                        content: data.content,
                        cardType: data.type,
                        chapterId,
                        projectId: projectId || ''
                    }, {
                        onSuccess: (newCard) => {
                            console.log('Card created successfully:', newCard);
                            setIsSaving(false);
                            toast({
                                title: "Card created",
                                description: `Card "${data.title}" has been created.`,
                                duration: 3000
                            });
                        },
                        onError: (error) => {
                            console.error('Error creating card:', error);
                            setIsSaving(false);
                            toast({
                                title: "Error creating card",
                                description: "There was an error creating the card. Please try again.",
                                variant: "destructive",
                                duration: 3000
                            });
                        }
                    });
                } catch (error) {
                    console.error('Error in dialog submit handler:', error);
                    setIsSaving(false);
                    toast({
                        title: "Error",
                        description: "An unexpected error occurred.",
                        variant: "destructive",
                        duration: 3000
                    });
                }
            }
        );
    }, [projectId, timelineState, createPlotCardMutation, toast]);

    // Add new function to handle chapter reordering

    // Add new function to handle timeline reordering

    // Add a handler for template application

    // Handler for one-shot template application from guided setup
    // const { data: currentStory } = useCurrentStory(projectId); // Removed redundant hook

    const handleApplyTemplateOneShot = useCallback(async (templateId: string) => {
        if (!projectId) return;

        // Ensure we have a timeline. If not, create one first or use the first available one.
        let targetTimelineId = selectedTimelineId;

        if (!targetTimelineId && timelineState?.plotLines && timelineState.plotLines.length > 0) {
            targetTimelineId = timelineState.plotLines[0].id;
        }

        // If still no timeline, create a default one first
        if (!targetTimelineId) {
            try {
                // Determine story ID with fallback to currentStory from hook
                const effectiveStoryId = currentStoryId || storyId || null;

                setIsApplyingTemplate(true);
                const { data: newTimeline, error: createError } = await supabase
                    .from('timelines')
                    .insert({
                        title: "Main Storyline",
                        description: "Initial story timeline",
                        story_id: effectiveStoryId,
                        is_primary: true
                    })
                    .select()
                    .single();

                if (createError) throw createError;
                targetTimelineId = newTimeline.id;
                console.log('Created new timeline for template:', targetTimelineId);
            } catch (err) {
                console.error('Error creating timeline for template:', err);
                toast({
                    title: "Error",
                    description: "Could not create a timeline to apply the template to.",
                    variant: "destructive"
                });
                setIsApplyingTemplate(false);
                return;
            }
        }

        try {
            setIsApplyingTemplate(true);
            toast({
                title: "Applying Template",
                description: "Setting up your story structure...",
            });

            const result = await templateService.applyTemplateToExistingTimeline(
                templateId,
                targetTimelineId as number
            );

            if (result.success) {
                toast({
                    title: "Structure Generated",
                    description: "Your story foundations have been built successfully.",
                });

                // Refresh data
                await refreshTimelineMutation.mutateAsync({ projectId });
                await refetchTimeline();

                // Once successfully applied, we can hide the guided setup
                setShowGuidedSetup(false);
                localStorage.setItem(`timeline-guided-setup-${projectId}`, 'false');
            } else {
                throw new Error(result.error || "Template application failed");
            }
        } catch (error) {
            console.error("Error applying template:", error);
            toast({
                title: "Application failed",
                description: error instanceof Error ? error.message : "There was an error generating your story structure.",
                variant: "destructive"
            });
        } finally {
            setIsApplyingTemplate(false);
        }
    }, [projectId, selectedTimelineId, timelineState?.plotLines, storyId, currentStoryId, refreshTimelineMutation, refetchTimeline, toast]);

    const handleDismissGuidedSetup = useCallback(() => {
        setShowGuidedSetup(false);
        localStorage.setItem(`timeline-guided-setup-${projectId}`, 'false');
    }, [projectId]);

    const handleStartFresh = useCallback(async () => {
        console.log('handleStartFresh called - opening dialog');
        // Always open the dialog to let user define name and chapters
        setIsCreateWithChaptersDialogOpen(true);
    }, []);

    const handleChapterTimelineCreated = useCallback(async (newTimelineId: number) => {
        console.log(`Timeline with chapters created: ID ${newTimelineId}`);
        // Refresh data
        if (projectId) {
            await refreshTimelineMutation.mutateAsync({ projectId });
            await refetchTimeline();
        }

        // Hide guided setup
        setShowGuidedSetup(false);
        localStorage.setItem(`timeline-guided-setup-${projectId}`, 'false');

        // Show success
        toast({
            title: "Timeline Ready",
            description: "Your new timeline has been created."
        });
    }, [projectId, refreshTimelineMutation, refetchTimeline, toast]);

    // Handler for when a template has been applied
    const handleTemplateApplied = useCallback(async () => {
        try {
            console.log('Template applied to timeline ID:', selectedTimelineId);
            if (!projectId) return;

            setIsSaving(true);
            toast({
                title: "Template Applied",
                description: "Refreshing timeline data...",
            });

            // Refresh the timeline data to show the template changes
            await refreshTimelineMutation.mutateAsync({
                projectId: projectId || ''
            });

            // Explicitly refetch the timeline to ensure we get the latest data
            await refetchTimeline();

            toast({
                title: "Template Applied",
                description: "The template has been successfully applied to your timeline.",
            });
        } catch (error) {
            console.error("Error refreshing timeline after template application:", error);
            toast({
                title: "Error",
                description: "Template was applied but we couldn't refresh the timeline. Please refresh manually.",
                variant: "destructive"
            });
        } finally {
            // Reset the selected timeline ID
            setSelectedTimelineId(null);
            setIsSaving(false);
        }
    }, [projectId, refreshTimelineMutation, refetchTimeline, toast, selectedTimelineId]);

    // Add handlers for chapter and plotline editing/deletion
    const handleEditChapter = useCallback(async (chapterId: number, data: { name: string; description: string; outline?: string }) => {
        if (!projectId || !timelineState?.storyId) return;

        try {
            console.log('Editing chapter:', chapterId, data);
            setIsSaving(true);

            // Use the updateChapter mutation with proper cache invalidation
            await updateChapterMutation.mutateAsync({
                chapterId,
                title: data.name,
                description: data.description,
                projectId
            });

            // If outline is provided, update the chapter outline
            if (data.outline !== undefined) {
                try {
                    const { updateChapterOutlineByChapterId } = await import('@/services/supabase/chapter-outlines');
                    await updateChapterOutlineByChapterId(chapterId, data.outline);
                    console.log('Chapter outline updated successfully');
                } catch (outlineError) {
                    console.error('Error updating chapter outline:', outlineError);
                    // Don't fail the entire operation if outline update fails
                }
            }

            console.log('Chapter updated successfully');
            setIsSaving(false);
            toast({
                title: "Chapter updated",
                description: `Chapter "${data.name}" has been updated.`,
                duration: 3000
            });
        } catch (error) {
            console.error('Error updating chapter:', error);
            setIsSaving(false);
            toast({
                title: "Error updating chapter",
                description: "There was an error updating the chapter. Please try again.",
                variant: "destructive",
                duration: 3000
            });
        }
    }, [projectId, updateChapterMutation, toast]);

    // handleDeleteChapter has been moved up before handleConfirmBulkDelete

    const handleEditPlotLine = useCallback(async (plotLineId: number, data: { title: string }) => {
        if (!projectId || !timelineState?.storyId) return;

        try {
            console.log('Editing plot line:', plotLineId, data);
            setIsSaving(true);

            // Use the updatePlotLineMutation that was defined at the top level of the component
            await updatePlotLineMutation.mutateAsync({
                lineId: plotLineId.toString(),
                updates: {
                    name: data.title
                },
                projectId: projectId || ''
            });

            toast({
                title: "Plot line updated",
                description: `Plot line "${data.title}" has been updated.`,
                duration: 3000
            });
        } catch (error) {
            console.error('Error updating plot line:', error);
            toast({
                title: "Error updating plot line",
                description: "There was an error updating the plot line. Please try again.",
                variant: "destructive",
                duration: 3000
            });
        } finally {
            setIsSaving(false);
        }
    }, [projectId, timelineState?.storyId, toast, updatePlotLineMutation]);

    // handleDeletePlotLine has been moved up before handleConfirmBulkDelete

    // AI improvement handlers - Removed

    // Unified Premium Header
    const renderHeader = () => (
        <header className="flex-none border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/75 sticky top-0 z-50">
            <div className="px-6 h-16 flex items-center justify-between">
                {/* Left: Title */}
                <h1 className="text-xl font-semibold tracking-tight text-foreground/90">Timeline</h1>

                {/* Right: Consolidated Controls */}
                <div className="flex items-center gap-2 sm:gap-4">
                    {/* Search & Filter & Sort Group */}
                    <div className="flex items-center gap-1 mr-2">
                        <UnifiedSearch
                            value={searchFilter}
                            onChange={setSearchFilter}
                            className="mr-2"
                        />

                        <TimelineFilter
                            projectId={projectId || ''}
                            selectedEntities={selectedEntities}
                            selectedTags={selectedTags}
                            onEntitySelect={handleEntitySelect}
                            onEntityRemove={handleEntityRemove}
                            onTagSelect={handleTagSelect}
                            onTagRemove={handleTagRemove}
                            onClearFilters={handleClearFilters}
                        />

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-xl"
                                >
                                    <ArrowUpDown className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setShowReorderTimelinesDialog(true)}>
                                    Sort Timelines
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setShowReorderChaptersDialog(true)}>
                                    Sort Chapters
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <div className="h-4 w-px bg-border mx-1" />

                    {/* View Controls Group */}
                    <div className="flex items-center gap-1">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant={compactMode ? "secondary" : "ghost"}
                                        size="icon"
                                        className={cn(
                                            "h-9 w-9 rounded-xl",
                                            compactMode ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
                                        )}
                                        onClick={() => setCompactMode(!compactMode)}
                                    >
                                        <Rows className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="z-[100]">
                                    {compactMode ? "Switch to Expanded View" : "Switch to Compact View"}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>

                        <div className="flex items-center bg-muted/30 rounded-xl p-1 border border-border/40 shadow-sm">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 hover:bg-background/80 rounded-lg transition-all"
                                onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
                                disabled={zoomLevel <= 50}
                            >
                                <ZoomOut className="h-3.5 w-3.5" />
                            </Button>
                            <span className="text-xs w-10 text-center font-mono text-muted-foreground">{zoomLevel}%</span>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 hover:bg-background/80 rounded-lg transition-all"
                                onClick={() => setZoomLevel(Math.min(150, zoomLevel + 10))}
                                disabled={zoomLevel >= 150}
                            >
                                <ZoomIn className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>

                    <div className="h-4 w-px bg-border mx-1" />

                    {/* Management Controls (Timelines & Chapters) */}
                    <div className="flex items-center gap-1">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-xl"
                                        onClick={() => setIsCreateTimelineDialogOpen(true)}
                                    >
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="z-[100]">Add Timeline</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-xl"
                                        onClick={handleAddChapter}
                                        disabled={!storyId}
                                    >
                                        <BookAIcon className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="z-[100]">Add Chapter</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>

                    <div className="h-4 w-px bg-border mx-1" />

                    {/* System Controls */}
                    <div className="flex items-center gap-1">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={handleRefreshData}
                                        className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                    >
                                        <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="z-[100]">Refresh Data</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                        onClick={() => setShowHelp(true)}
                                    >
                                        <HelpCircle className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="z-[100]">Help (F1)</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </div>
        </header>
    );

    // Update renderTimelineContent to handle null data gracefully
    const renderTimelineContent = () => {
        // Check if we have the necessary data to render the timeline
        const hasTimelines = timelineState?.plotLines && timelineState.plotLines.length > 0;
        const hasChapters = timelineState?.chapters && timelineState.chapters.length > 0;

        // If we're missing crucial data, show the guided setup view
        // This is now the default empty state as requested
        // If we're missing crucial data, show the guided setup view
        // Show if we're missing content (timelines or chapters) regardless of preference
        // This acts as the empty state for the timeline
        if ((!hasTimelines || !hasChapters) && showGuidedSetup) {
            return (
                <div className="flex flex-col items-center min-h-[calc(100vh-16rem)] w-full">
                    <div className="w-full mx-auto">
                        <TimelineGuidedSetup
                            onApplyTemplate={handleApplyTemplateOneShot}
                            onStartFresh={handleStartFresh}
                            onClose={handleDismissGuidedSetup}
                            isApplying={isApplyingTemplate}
                        />
                        <CreateTimelineWithChaptersDialog
                            open={isCreateWithChaptersDialogOpen}
                            onOpenChange={setIsCreateWithChaptersDialogOpen}
                            // storyId and projectId removed as they are now handled internally via hooks
                            onCreated={handleChapterTimelineCreated}
                        />
                    </div>
                </div>
            );
        }

        if (viewMode === "grid") {
            return (
                <TimelineGrid
                    plotLines={timelineState?.plotLines || []}
                    chapters={timelineState?.chapters || []}
                    cards={filteredCards}
                    onCardSelect={handleSelectCard}
                    onAddCard={handleAddPlotCard}
                    onEditCard={(card) => handleShowCardDetails(card)}
                    onShowDetails={handleShowCardDetails}
                    onDeleteCard={handleDeleteCard}
                    onCopyCard={handleDuplicateCard}
                    onMoveCard={handleMoveCard}
                    selectedItems={selectedItems}
                    compactMode={compactMode}
                    onEditChapter={handleEditChapter}
                    onDeleteChapter={handleDeleteChapter}
                    onAddChapter={handleAddChapter}
                    onEditPlotLine={handleEditPlotLine}
                    onDeletePlotLine={handleDeletePlotLine}
                    onReorderTimelines={() => setShowReorderTimelinesDialog(true)}
                    onReorderChapters={() => setShowReorderChaptersDialog(true)}
                    onApplyTemplate={(timelineId) => {
                        console.log(`Applying template to timeline: ${timelineId}`);
                        // Store the selected timeline ID in state so we can use it when applying the template
                        setSelectedTimelineId(timelineId);
                        setIsTemplateDialogOpen(true);
                    }}
                    // Selection mode props
                    selectedChapters={selectedChapters}
                    selectedTimelines={selectedTimelines}
                    onChapterSelect={handleChapterSelection}
                    onTimelineSelect={handleTimelineSelection}
                />
            );
        }

        // Future implementation of other view modes
        return <div className="p-4">Other view modes coming soon...</div>;
    };

    // Add keyboard event handler
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Escape to clear all selections
            if (e.key === 'Escape') {
                setSelectedItems([]);
                setSelectedTimelines([]);
                setSelectedChapters([]);
                return;
            }

            // Delete selected items with Delete key
            if (e.key === 'Delete' || e.key === 'Backspace') {
                // Check if any items are selected and handle accordingly
                if (selectedItems.length > 0) {
                    handleDeleteSelectedItems();
                    return;
                }

                if (selectedTimelines.length > 0) {
                    handleBulkDeleteTimelines();
                    return;
                }

                if (selectedChapters.length > 0) {
                    handleBulkDeleteChapters();
                    return;
                }
            }

            // Toggle compact mode with Ctrl+M
            if (e.ctrlKey && e.key.toLowerCase() === 'm') {
                e.preventDefault();
                setCompactMode((prev: boolean) => !prev);
            }

            // Toggle help dialog with F1 or Ctrl+H
            if (e.key === 'F1' || (e.ctrlKey && e.key.toLowerCase() === 'h')) {
                e.preventDefault();
                setShowHelp(prev => !prev);
            }

            // Ctrl+T to open the template dialog
            if (e.ctrlKey && e.key === 't') {
                e.preventDefault();
                setIsTemplateDialogOpen(true);
            }

            // Ctrl+Shift+N to create a new timeline
            if (e.ctrlKey && e.shiftKey && e.key === 'n') {
                e.preventDefault();
                setIsCreateTimelineDialogOpen(true);
            }

            // Ctrl+A to select all items of the current type
            if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
                // Only if not in an input field
                if (!['INPUT', 'TEXTAREA'].includes((document.activeElement?.tagName || '').toUpperCase())) {
                    // Prevent default browser behavior
                    e.preventDefault();

                    // Determine what to select based on what's currently visible/focused
                    // For now, we'll just implement this for cards
                    if (timelineState?.plotCards && timelineState.plotCards.length > 0) {
                        setSelectedItems(timelineState.plotCards);
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedItems, selectedTimelines, selectedChapters, timelineState?.plotCards, handleDeleteSelectedItems, handleBulkDeleteTimelines, handleBulkDeleteChapters]);





    // Add this effect near the other useEffect hooks
    useEffect(() => {
        // Reset saving state when component unmounts
        return () => {
            setIsSaving(false);
            setDetailsDrawerOpen(false);
            setCardToViewDetails(null);
        };
    }, []);

    // Add this useEffect for keyboard shortcut to focus search


    // Card selection handler - only used for bulk operations
    const handleSelectCard = useCallback((card: PlotCard, isMultiSelect: boolean = false) => {
        setSelectedItems(prev => {
            const isSelected = prev.some(item => item.id === card.id);

            if (isMultiSelect) {
                // Multi-select mode (e.g., with Shift key)
                if (isSelected) {
                    return prev.filter(item => item.id !== card.id);
                } else {
                    return [...prev, card];
                }
            } else {
                // Single select mode
                if (isSelected && prev.length === 1) {
                    return [];
                } else {
                    return [card];
                }
            }
        });

        // Clear other selections when selecting cards
        if (selectedTimelines.length > 0) {
            setSelectedTimelines([]);
        }
        if (selectedChapters.length > 0) {
            setSelectedChapters([]);
        }
    }, [selectedTimelines, selectedChapters]);


    const handleDeleteCard = useCallback((card: PlotCard) => {
        console.warn("handleDeleteCard called, but not fully implemented");
        if (!card || !card.id) return;

        if (!timelineState?.storyId) {
            console.error("Cannot delete card: No story ID available");
            return;
        }

        try {
            setIsSaving(true);
            deletePlotCardMutation.mutate({
                cardId: card.id.toString(),
                timelineId: timelineState.storyId,
                projectId: projectId || ''
            }, {
                onSuccess: () => {
                    console.log("Card deleted successfully");
                    setIsSaving(false);
                },
                onError: (error) => {
                    console.error("Error deleting card:", error);
                    setIsSaving(false);
                }
            });

            // If the deleted card was selected, deselect it
            setSelectedItems(prev => prev.filter(item => item.id !== card.id));
        } catch (error) {
            console.error("Error in handleDeleteCard:", error);
            setIsSaving(false);
        }
    }, [timelineState?.storyId, deletePlotCardMutation, projectId]);

    const handleDuplicateCard = useCallback((card: PlotCard) => {
        if (!projectId || !timelineState?.storyId) return;

        try {
            console.log('Duplicating card:', card);
            setIsSaving(true);

            // Use the createPlotCardMutation to create a new card with the same properties
            createPlotCardMutation.mutate({
                timelineId: timelineState.storyId,
                plotLineId: card.x.toString(),
                title: `${card.title} (Copy)`,
                content: card.content || '',
                cardType: card.type || 'note',
                chapterId: card.chapter_id || undefined,
                projectId: projectId || ''
            }, {
                onSuccess: (newCard) => {
                    console.log('Card duplicated successfully:', newCard);
                    setIsSaving(false);
                    toast({
                        title: "Card duplicated",
                        description: `Card "${card.title}" has been duplicated.`,
                        duration: 3000
                    });
                },
                onError: (error) => {
                    console.error('Error duplicating card:', error);
                    setIsSaving(false);
                    toast({
                        title: "Error duplicating card",
                        description: "There was an error duplicating the card. Please try again.",
                        variant: "destructive",
                        duration: 3000
                    });
                }
            });
        } catch (error) {
            console.error('Error in handleDuplicateCard:', error);
            setIsSaving(false);
            toast({
                title: "Error",
                description: "An unexpected error occurred while duplicating the card.",
                variant: "destructive",
                duration: 3000
            });
        }
    }, [projectId, timelineState?.storyId, createPlotCardMutation, toast]);

    // Helper function to update a card in the local state
    const updateCardInLocalState = useCallback((cardId: number, updates: Partial<PlotCard>) => {
        if (!timelineState) return;

        // Create a new timelineState object with the updated card
        const updatedCards = timelineState.plotCards.map(card => {
            if (card.id === cardId) {
                return { ...card, ...updates };
            }
            return card;
        });

        // Update the timelineState with the new cards array
        const updatedTimelineState = {
            ...timelineState,
            plotCards: updatedCards
        };

        // Use the queryClient to update the cache directly
        queryClient.setQueryData(
            secureTimelineKeys.timeline(projectId || ''),
            updatedTimelineState
        );
    }, [timelineState, projectId, queryClient]);

    // Handler for moving a card to a new position or chapter with optimistic updates
    const handleMoveCard = useCallback((cardId: number, newPlotLineId: number, newChapterId: number, newPosition?: number) => {
        if (!timelineState?.storyId) {
            console.error("Cannot move card: No story ID available");
            return;
        }

        console.log('Moving card:', { cardId, newPlotLineId, newChapterId, newPosition });

        // Find the card in the current state
        const cardToMove = timelineState.plotCards.find(card => card.id === cardId);
        if (!cardToMove) {
            console.error(`Card with ID ${cardId} not found in the current state`);
            return;
        }

        // Save the original card state for potential rollback
        const originalCard = { ...cardToMove };

        // 1. Optimistically update the local state immediately
        // Make sure to preserve the card type
        updateCardInLocalState(cardId, {
            x: newPlotLineId,
            chapter_id: newChapterId,
            position: newPosition,
            // Preserve the original card type
            type: cardToMove.type
        });

        // 2. Make the API call in the background
        updatePlotCardMutation.mutate({
            cardId: cardId.toString(),
            updates: {
                x: newPlotLineId,
                chapter_id: newChapterId,
                position: newPosition,
                // Include the original card type to ensure it's preserved
                type: cardToMove.type
            },
            timelineId: timelineState.storyId,
            projectId: projectId || ''
        }, {
            // 3. Handle success - no need to update the UI again as we've already done it
            onSuccess: () => {
                console.log('Card moved successfully');
                // No need to show a toast for successful drag and drop operations
                // as it would be distracting during rapid interactions
            },
            // 4. Handle error - revert the local state change
            onError: (error) => {
                console.error('Error moving card:', error);

                // Revert the optimistic update by restoring the original card
                updateCardInLocalState(cardId, originalCard);

                // Show an error toast
                toast({
                    title: "Error moving card",
                    description: "There was an error moving the card. The change has been reverted.",
                    variant: "destructive",
                    duration: 3000
                });
            }
        });
    }, [timelineState, updateCardInLocalState, updatePlotCardMutation, projectId, toast]);

    const handleSaveEditedCard = useCallback(async (card: PlotCard, updates: Partial<PlotCard>) => {
        console.warn("handleSaveEditedCard called with updates:", updates);
        try {
            if (!timelineState?.storyId) {
                throw new Error("Cannot save card: No story ID available");
            }

            setIsSaving(true);

            // 1. Optimistically update the local state immediately
            updateCardInLocalState(card.id, updates);

            // 2. Make sure we include the timeline_id in the updates to avoid foreign key constraint error
            const updatesWithTimeline = {
                ...updates,
                // Ensure we have the timeline_id (which is the plotline_id in our case)
                x: card.x // This is the plotline_id
            };

            // 3. Make the API call in the background
            await updatePlotCardMutation.mutateAsync({
                cardId: card.id.toString(),
                updates: updatesWithTimeline,
                timelineId: card.x.toString(), // Use the card's plotline_id as the timeline_id
                projectId: projectId || ''
            });

            toast({
                title: "Success",
                description: "Card updated successfully",
            });

            return Promise.resolve();
        } catch (error) {
            console.error("Error saving card:", error);
            toast({
                title: "Error updating card",
                description: error instanceof Error ? error.message : "An unknown error occurred",
                variant: "destructive"
            });
            return Promise.reject(error);
        } finally {
            setIsSaving(false);
        }
    }, [timelineState, updateCardInLocalState, updatePlotCardMutation, projectId, toast]);

    // Handler for showing card details
    const handleShowCardDetails = useCallback((card: PlotCard) => {
        // Find the plotline and chapter names for this card
        const plotline = timelineState?.plotLines?.find(pl => pl.id === card.x);
        const chapter = timelineState?.chapters?.find(ch => ch.id === card.chapter_id);

        // Create an enhanced card with plotline_name and chapter_name
        const enhancedCard = {
            ...card,
            plotline_name: plotline?.name || 'Unknown',
            chapter_name: chapter?.name || 'Not assigned'
        };

        setCardToViewDetails(enhancedCard);
        setDetailsDrawerOpen(true);
    }, [timelineState?.plotLines, timelineState?.chapters]);



    if (!isInitialized || isSessionLoading) {
        return <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-background text-foreground">
            <div className="text-center space-y-4">
                <div className="relative mx-auto h-12 w-12">
                    <div className="absolute inset-0 rounded-full border-2 border-primary/20"></div>
                    <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
                </div>
                <div>
                    <h2 className="text-lg font-medium tracking-tight">Loading session...</h2>
                    <p className="text-sm text-muted-foreground mt-1">Please wait while we initialize your session</p>
                </div>
            </div>
        </div>;
    }

    if (!session) {
        return <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-background text-foreground">
            <div className="text-center space-y-2">
                <h2 className="text-lg font-medium tracking-tight">Authentication Required</h2>
                <p className="text-sm text-muted-foreground">Please sign in to view the timeline</p>
            </div>
        </div>;
    }

    if (!projectId) {
        return <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-background text-foreground">
            <div className="text-center space-y-2">
                <h2 className="text-lg font-medium tracking-tight">No Project Selected</h2>
                <p className="text-sm text-muted-foreground">Please select a project to view its timeline</p>
            </div>
        </div>;
    }

    if (isLoading) {
        return (
            <div className="flex flex-col h-full max-h-screen bg-background text-foreground">
                {renderHeader()}
                <div className="flex-1 overflow-hidden p-4 sm:p-6">
                    <div className="w-full h-full overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
                        <div className="grid h-full" style={{
                            gridTemplateColumns: `auto repeat(3, minmax(280px, 1fr))`,
                            gridTemplateRows: 'auto 1fr'
                        }}>
                            {/* Header Row: Corner Cell + Chapter Headers */}
                            <div className="contents">
                                {/* Corner Cell (Plot Lines) */}
                                <div className="sticky top-0 left-0 z-30 bg-card border-b border-border/60">
                                    <div className="px-4 py-3 flex items-center justify-between">
                                        <Skeleton className="h-4 w-24 bg-muted/50" />
                                        <div className="flex gap-2">
                                            <Skeleton className="h-6 w-6 rounded-md bg-muted/50" />
                                            <Skeleton className="h-6 w-6 rounded-md bg-muted/50" />
                                        </div>
                                    </div>
                                </div>

                                {/* Chapter Header Skeletons */}
                                {[1, 2, 3].map((i) => (
                                    <div key={`header-${i}`} className="p-3 border-b border-r border-border/60 bg-card sticky top-0 z-20">
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <Skeleton className="h-5 w-32 bg-muted/50" />
                                                <Skeleton className="h-5 w-5 rounded-full bg-muted/50" />
                                            </div>
                                            <Skeleton className="h-3 w-48 bg-muted/30" />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Grid Body */}
                            <div className="contents">
                                {/* Plot Lines Column */}
                                <div className="border-r border-border/60 bg-card/50">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <div key={`row-${i}`} className="h-40 p-4 border-b border-border/60 flex items-center sticky left-0 z-10 bg-card/95">
                                            <Skeleton className="h-5 w-24 bg-muted/50" />
                                        </div>
                                    ))}
                                </div>

                                {/* Plot Cards Cells */}
                                {[1, 2, 3].map((col) => (
                                    <div key={`col-${col}`} className="border-r border-border/60">
                                        {[1, 2, 3, 4, 5].map((row) => (
                                            <div key={`cell-${col}-${row}`} className="h-40 p-4 border-b border-border/60 bg-muted/5">
                                                {/* Randomly sparsely populate cells with card skeletons */}
                                                {(col + row) % 2 === 0 && (
                                                    <div className="h-32 rounded-xl border border-border/40 bg-card p-4 space-y-3">
                                                        <Skeleton className="h-4 w-3/4 bg-muted/50" />
                                                        <Skeleton className="h-16 w-full bg-muted/30 rounded-lg" />
                                                        <div className="flex gap-2">
                                                            <Skeleton className="h-3 w-12 rounded-full bg-muted/40" />
                                                            <Skeleton className="h-3 w-12 rounded-full bg-muted/40" />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (timelineError) {
        // Check if it's a "no timeline exists" type of error
        const isNoTimelineError =
            timelineError instanceof Error &&
            (timelineError.message.includes('No timeline data found') ||
                timelineError.message.includes('No timeline found') ||
                timelineError.message.includes('Could not find any timeline') ||
                timelineError.message.includes('No timeline found in secure data'));

        // If it's NOT a "no timeline" error, show the error screen
        // If it IS a "no timeline" error, we proceed to render the main UI (which handles empty states)
        if (!isNoTimelineError) {
            return (
                <div className="flex items-center justify-center h-full bg-background text-foreground">
                    <div className="text-center">
                        <h2 className="text-lg font-semibold mb-2">Error Loading Timeline</h2>
                        <p className="text-red-500">{timelineError instanceof Error ? timelineError.message : "Unknown error occurred"}</p>
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => refetchTimeline()}
                        >
                            Retry
                        </Button>
                    </div>
                </div>
            );
        }
    }



    return (
        <DndProvider backend={HTML5Backend}>
            <TooltipProvider>
                <div className="flex flex-col h-full max-h-screen bg-background text-foreground">
                    {/* Streamlined Unified Header */}
                    {renderHeader()}

                    {/* Main Content Area */}
                    <div className="flex-1 overflow-auto scrollbar-hide p-4 sm:p-6" ref={timelineRef}>

                        {/* Results Summary - more consistent styling */}
                        {(searchFilter || selectedEntities.length > 0 || selectedTags.length > 0) && (
                            <div className="mb-3 px-3 py-2 bg-muted/40 rounded-md text-sm flex flex-wrap items-center gap-y-2">
                                <span className="text-muted-foreground mr-2">
                                    {filteredCards.length > 0
                                        ? `Showing ${filteredCards.length} results`
                                        : `No results found`
                                    }
                                    {searchFilter && ` for "${searchFilter}"`}
                                    {(selectedEntities.length > 0 || selectedTags.length > 0) && (
                                        <span>
                                            {searchFilter ? ' with ' : ' for '}
                                            {selectedEntities.length > 0 && `${selectedEntities.length} entity filter${selectedEntities.length !== 1 ? 's' : ''}`}
                                            {selectedEntities.length > 0 && selectedTags.length > 0 && ' and '}
                                            {selectedTags.length > 0 && `${selectedTags.length} tag filter${selectedTags.length !== 1 ? 's' : ''}`}
                                        </span>
                                    )}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 ml-auto text-xs"
                                    onClick={() => {
                                        setSearchFilter("");
                                        setSelectedEntities([]);
                                        setSelectedTags([]);
                                    }}
                                >
                                    Clear All
                                </Button>
                            </div>
                        )}

                        {/* Selection Status - more consistent styling */}
                        {selectedItems.length > 0 && (
                            <div className="mb-3 px-3 py-2 bg-primary/10 rounded-md text-sm flex flex-wrap items-center gap-y-2">
                                <span className="font-medium mr-2">
                                    {selectedItems.length} card{selectedItems.length !== 1 ? 's' : ''} selected
                                </span>
                                <div className="flex flex-wrap gap-2 ml-auto mt-1 sm:mt-0">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-xs"
                                        onClick={handleMoveSelectedCards}
                                    >
                                        Move Cards
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-xs text-destructive"
                                        onClick={handleDeleteSelectedItems}
                                    >
                                        Delete
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-xs"
                                        onClick={() => setSelectedItems([])}
                                    >
                                        Clear Selection
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className="relative" style={{
                            transform: `scale(${zoomLevel / 100})`,
                            transformOrigin: 'top left',
                            width: 'fit-content',
                            minWidth: '100%'
                        }}>
                            {renderTimelineContent()}
                        </div>



                        {/* Bulk Actions Bar for Cards */}
                        {selectedItems.length > 0 && (
                            <BulkActionsBar
                                selectedCount={selectedItems.length}
                                itemType="cards"
                                onClearSelection={() => setSelectedItems([])}
                                onDelete={handleDeleteSelectedItems}
                                onDuplicate={() => console.log('Duplicate cards not implemented yet')}
                            />
                        )}
                    </div>


                </div>

                {/* Bulk Actions Bar for Timelines */}
                {
                    selectedTimelines.length > 0 && (
                        <BulkActionsBar
                            selectedCount={selectedTimelines.length}
                            itemType="timelines"
                            onClearSelection={() => setSelectedTimelines([])}
                            onDelete={handleBulkDeleteTimelines}
                            onReorder={() => setShowReorderTimelinesDialog(true)}
                        />
                    )
                }

                {/* Bulk Actions Bar for Chapters */}
                {
                    selectedChapters.length > 0 && (
                        <BulkActionsBar
                            selectedCount={selectedChapters.length}
                            itemType="chapters"
                            onClearSelection={() => setSelectedChapters([])}
                            onDelete={handleBulkDeleteChapters}
                            onReorder={() => setShowReorderChaptersDialog(true)}
                        />
                    )
                }

                {/* Dialogs and Drawers */}
                <PlotCardDetailsModal
                    open={detailsDrawerOpen}
                    onOpenChange={setDetailsDrawerOpen}
                    card={cardToViewDetails}
                    onSave={handleSaveEditedCard}
                    onDelete={(card: PlotCard) => {
                        setDetailsDrawerOpen(false);
                        handleDeleteCard(card);
                    }}
                    onDuplicate={(card: PlotCard) => {
                        setDetailsDrawerOpen(false);
                        handleDuplicateCard(card);
                    }}
                />
                <HelpDialog
                    open={showHelp}
                    onOpenChange={setShowHelp}
                    keyboardShortcuts={[
                        { key: 'Ctrl+M', action: 'Toggle compact mode' },
                        { key: 'Delete', action: 'Delete selected cards' },
                        { key: 'F1 or Ctrl+H', action: 'Open help' },
                        { key: 'Ctrl+F', action: 'Focus search' },
                        { key: 'Escape', action: 'Clear search or close dialogs' }
                    ]}
                />





                {/* PlotCardEditDrawer removed - functionality integrated into modal */}

                <ReorderChaptersDialog
                    open={showReorderChaptersDialog}
                    onOpenChange={setShowReorderChaptersDialog}
                    chapters={timelineState?.chapters || []}
                    onReorderChapters={(chapterIds) => {
                        // Only proceed if we have story ID and chapters
                        if (timelineState?.storyId && chapterIds.length > 0) {
                            setIsSaving(true);
                            console.log('Reordering chapters using batch function:', chapterIds);
                            reorderChaptersMutation.mutate({
                                storyId: timelineState.storyId,
                                chapterIds: chapterIds,
                                projectId: projectId || ''
                            }, {
                                onSuccess: () => {
                                    toast({
                                        title: "Success",
                                        description: "Chapters reordered successfully",
                                        duration: 3000
                                    });
                                    setShowReorderChaptersDialog(false);
                                    setIsSaving(false);
                                },
                                onError: (error: Error) => {
                                    console.error('Error reordering chapters:', error);
                                    toast({
                                        title: "Error",
                                        description: error.message || "Failed to reorder chapters",
                                        variant: "destructive",
                                        duration: 5000
                                    });
                                    setIsSaving(false);
                                }
                            });
                        }
                    }}
                    isSaving={isSaving}
                />

                <ReorderTimelinesDialog
                    open={showReorderTimelinesDialog}
                    onOpenChange={setShowReorderTimelinesDialog}
                    timelines={timelineState?.plotLines || []}
                    onReorderTimelines={(timelineIds) => {
                        // Only proceed if we have story ID and timelines
                        if (timelineState?.storyId && timelineIds.length > 0) {
                            setIsSaving(true);
                            console.log('Reordering timelines using batch function:', timelineIds);
                            reorderTimelinesMutation.mutate({
                                storyId: timelineState.storyId,
                                timelineIds: timelineIds,
                                projectId: projectId || ''
                            }, {
                                onSuccess: () => {
                                    toast({
                                        title: "Success",
                                        description: "Timelines reordered successfully",
                                        duration: 3000
                                    });
                                    setShowReorderTimelinesDialog(false);
                                    setIsSaving(false);
                                },
                                onError: (error: Error) => {
                                    console.error('Error reordering timelines:', error);
                                    toast({
                                        title: "Error",
                                        description: error.message || "Failed to reorder timelines",
                                        variant: "destructive",
                                        duration: 5000
                                    });
                                    setIsSaving(false);
                                }
                            });
                        }
                    }}
                    isSaving={isSaving}
                />

                <TemplateDialog
                    open={isTemplateDialogOpen}
                    onOpenChange={setIsTemplateDialogOpen}
                    timelineId={selectedTimelineId || (timelineState?.timeline?.id ? parseInt(timelineState.timeline.id.toString()) : 0)}
                    onApplied={handleTemplateApplied}
                />

                {/* Bulk Delete Dialog */}
                <BulkDeleteDialog
                    open={showBulkDeleteDialog.open}
                    onOpenChange={(open) => setShowBulkDeleteDialog({ ...showBulkDeleteDialog, open })}
                    itemType={showBulkDeleteDialog.type}
                    itemCount={showBulkDeleteDialog.type === 'timelines' ? selectedTimelines.length :
                        showBulkDeleteDialog.type === 'chapters' ? selectedChapters.length :
                            selectedItems.length}
                    onConfirm={handleConfirmBulkDelete}
                    isDeleting={isSaving}
                />

                {/* Selection Panel for Timelines and Chapters */}
                <SelectionPanel
                    open={isSelectionPanelOpen}
                    onOpenChange={setIsSelectionPanelOpen}
                    plotLines={timelineState?.plotLines || []}
                    chapters={timelineState?.chapters || []}
                    selectedTimelines={selectedTimelines}
                    selectedChapters={selectedChapters}
                    onTimelineSelect={handleTimelineSelection}
                    onChapterSelect={handleChapterSelection}
                    onSelectAllTimelines={handleSelectAllTimelines}
                    onSelectAllChapters={handleSelectAllChapters}
                    onClearTimelineSelection={() => setSelectedTimelines([])}
                    onClearChapterSelection={() => setSelectedChapters([])}
                    onAddTimeline={() => setIsCreateTimelineDialogOpen(true)}
                    onAddChapter={handleAddChapter}
                    onReorderTimelines={() => setShowReorderTimelinesDialog(true)}
                    onReorderChapters={() => setShowReorderChaptersDialog(true)}
                    onDeleteTimelines={handleBulkDeleteTimelines}
                    onDeleteChapters={handleBulkDeleteChapters}
                />

                <CreateTimelineDialog
                    open={isCreateTimelineDialogOpen}
                    onOpenChange={setIsCreateTimelineDialogOpen}
                    onCreated={(timelineId) => {
                        console.log(`Timeline created with ID: ${timelineId}`);
                        // Refresh timeline data
                        handleRefreshTimeline();
                        toast({
                            title: "Success",
                            description: "Timeline created successfully",
                        });
                    }}
                />




                {/* AI Timeline Improvement Dialog */}
                {/* AI Timeline Improvement Dialog - Removed */}

            </TooltipProvider >
        </DndProvider >
    );
}